import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Container,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Chip,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Avatar,
} from '@mui/material';
import {
  Assessment,
  Warning,
  CheckCircle,
  ErrorOutline,
  Insights,
  Analytics as AnalyticsIcon,
  Shield,
  Report,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useAppSelector } from '../store/hooks';

const StyledCard = styled(Card)(({ theme }) => ({
  transition: 'all 0.3s ease-in-out',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: theme.shadows[6],
  },
}));

const MetricCard = styled(Card)(({ theme }) => ({
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  color: 'white',
  '& .MuiTypography-root': {
    color: 'white',
  },
}));

const RiskCard = styled(Card)(({ theme, risk }: { theme?: any; risk: string }) => {
  const colors = {
    low: 'linear-gradient(135deg, #4CAF50 0%, #45a049 100%)',
    medium: 'linear-gradient(135deg, #FF9800 0%, #f57c00 100%)',
    high: 'linear-gradient(135deg, #f44336 0%, #d32f2f 100%)',
  };
  
  return {
    background: colors[risk as keyof typeof colors] || colors.medium,
    color: 'white',
    '& .MuiTypography-root': {
      color: 'white',
    },
  };
});

interface AnalyticsData {
  report_period: string;
  summary: {
    total_analyses: number;
    counterfeit_rate: number;
    average_confidence: number;
    high_risk_products: number;
  };
  trends: {
    counterfeit_rate: number;
    confidence_trend: number[];
    risk_distribution: {
      low: number;
      medium: number;
      high: number;
    };
  };
  insights: {
    most_counterfeited_brands: Array<{ brand: string; count: number }>;
    most_counterfeited_categories: Array<{ category: string; count: number }>;
    detection_accuracy: number;
    improvement_recommendations: string[];
  };
}

interface RiskAssessmentData {
  overall_risk_score: number;
  risk_factors: Array<{
    factor: string;
    score: number;
    description: string;
  }>;
  recommendations: string[];
  risk_trends: {
    daily: number[];
    weekly: number[];
    monthly: number[];
  };
}

const AnalyticsPage: React.FC = () => {
  const { loading, error } = useAppSelector((state) => state.dashboard);
  
  // Mock functions for API calls - replace with actual Redux thunks when available
  const getAnalytics = async (filters?: any) => ({ analytics: null });
  const getRiskAssessment = async (filters?: any) => ({ risk_assessment: null });
  
  // State
  const [analyticsData, setAnalyticsData] = useState<AnalyticsData | null>(null);
  const [riskData, setRiskData] = useState<RiskAssessmentData | null>(null);
  const [selectedPeriod, setSelectedPeriod] = useState('30');
  const [selectedRiskLevel, setSelectedRiskLevel] = useState('all');

  // Load analytics data
  const loadAnalytics = useCallback(async () => {
    try {
      const filters = {
        period_days: selectedPeriod,
        ...(selectedRiskLevel !== 'all' && { risk_level: selectedRiskLevel })
      };
      
      const [analyticsResult, riskResult] = await Promise.all([
        getAnalytics(filters),
        getRiskAssessment(filters)
      ]);
      
      setAnalyticsData(analyticsResult.analytics);
      setRiskData(riskResult.risk_assessment);
    } catch (err) {
      console.error('Failed to load analytics:', err);
    }
  }, [selectedPeriod, selectedRiskLevel, getAnalytics, getRiskAssessment]);

  // Load data on component mount and when filters change
  useEffect(() => {
    loadAnalytics();
  }, [loadAnalytics]);

  // Render metric card
  const renderMetricCard = (title: string, value: string | number, subtitle?: string, icon?: React.ReactNode) => (
    <StyledCard>
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography variant="h6" color="textSecondary" gutterBottom>
              {title}
            </Typography>
            <Typography variant="h4" component="div" fontWeight="bold">
              {value}
            </Typography>
            {subtitle && (
              <Typography variant="body2" color="textSecondary">
                {subtitle}
              </Typography>
            )}
          </Box>
          {icon && (
            <Avatar sx={{ bgcolor: 'primary.main', width: 56, height: 56 }}>
              {icon}
            </Avatar>
          )}
        </Box>
      </CardContent>
    </StyledCard>
  );



  // Render progress bar
  const renderProgressBar = (value: number, max: number = 100, color: string = 'primary') => (
    <Box sx={{ width: '100%', mt: 1 }}>
      <LinearProgress
        variant="determinate"
        value={(value / max) * 100}
        color={color as any}
        sx={{ height: 8, borderRadius: 4 }}
      />
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
        <Typography variant="body2" color="textSecondary">
          {value}
        </Typography>
        <Typography variant="body2" color="textSecondary">
          {max}
        </Typography>
      </Box>
    </Box>
  );

  if (loading && !analyticsData) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress size={60} />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box mb={4}>
        <Typography variant="h4" component="h1" gutterBottom fontWeight="bold">
          Analytics & Insights
        </Typography>
        <Typography variant="h6" color="textSecondary">
          Comprehensive analysis of counterfeit detection performance
        </Typography>
      </Box>

      {/* Filters */}
      <StyledCard sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Filters
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Time Period</InputLabel>
                <Select
                  value={selectedPeriod}
                  onChange={(e) => setSelectedPeriod(e.target.value)}
                  label="Time Period"
                >
                  <MenuItem value="7">Last 7 days</MenuItem>
                  <MenuItem value="30">Last 30 days</MenuItem>
                  <MenuItem value="90">Last 90 days</MenuItem>
                  <MenuItem value="365">Last year</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Risk Level</InputLabel>
                <Select
                  value={selectedRiskLevel}
                  onChange={(e) => setSelectedRiskLevel(e.target.value)}
                  label="Risk Level"
                >
                  <MenuItem value="all">All Risk Levels</MenuItem>
                  <MenuItem value="low">Low Risk</MenuItem>
                  <MenuItem value="medium">Medium Risk</MenuItem>
                  <MenuItem value="high">High Risk</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </StyledCard>

      {error && (
        <Alert severity="error" sx={{ mb: 4 }}>
          <Typography variant="h6">Error Loading Analytics</Typography>
          <Typography>{error}</Typography>
        </Alert>
      )}

      {/* Summary Metrics */}
      {analyticsData && (
        <Box mb={4}>
          <Typography variant="h5" gutterBottom fontWeight="bold">
            Summary Metrics
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={3}>
              {renderMetricCard(
                'Total Analyses',
                analyticsData.summary.total_analyses.toLocaleString(),
                analyticsData.report_period,
                <AnalyticsIcon />
              )}
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              {renderMetricCard(
                'Counterfeit Rate',
                `${(analyticsData.summary.counterfeit_rate * 100).toFixed(1)}%`,
                'Products flagged as counterfeit',
                <ErrorOutline />
              )}
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              {renderMetricCard(
                'Average Confidence',
                `${(analyticsData.summary.average_confidence * 100).toFixed(1)}%`,
                'Detection confidence',
                <Assessment />
              )}
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              {renderMetricCard(
                'High Risk Products',
                analyticsData.summary.high_risk_products.toLocaleString(),
                'Requiring immediate attention',
                <Warning />
              )}
            </Grid>
          </Grid>
        </Box>
      )}

      {/* Risk Distribution */}
      {analyticsData && (
        <Box mb={4}>
          <Typography variant="h5" gutterBottom fontWeight="bold">
            Risk Distribution
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={4}>
              <RiskCard risk="low">
                <CardContent>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box>
                      <Typography variant="h6">Low Risk</Typography>
                      <Typography variant="h4" fontWeight="bold">
                        {analyticsData.trends.risk_distribution.low}
                      </Typography>
                    </Box>
                    <CheckCircle sx={{ fontSize: 40 }} />
                  </Box>
                </CardContent>
              </RiskCard>
            </Grid>
            <Grid item xs={12} sm={4}>
              <RiskCard risk="medium">
                <CardContent>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box>
                      <Typography variant="h6">Medium Risk</Typography>
                      <Typography variant="h4" fontWeight="bold">
                        {analyticsData.trends.risk_distribution.medium}
                      </Typography>
                    </Box>
                    <Warning sx={{ fontSize: 40 }} />
                  </Box>
                </CardContent>
              </RiskCard>
            </Grid>
            <Grid item xs={12} sm={4}>
              <RiskCard risk="high">
                <CardContent>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box>
                      <Typography variant="h6">High Risk</Typography>
                      <Typography variant="h4" fontWeight="bold">
                        {analyticsData.trends.risk_distribution.high}
                      </Typography>
                    </Box>
                    <ErrorOutline sx={{ fontSize: 40 }} />
                  </Box>
                </CardContent>
              </RiskCard>
            </Grid>
          </Grid>
        </Box>
      )}

      {/* Insights and Recommendations */}
      {analyticsData && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <StyledCard>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  <Insights sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Most Counterfeited Brands
                </Typography>
                <List>
                  {analyticsData.insights.most_counterfeited_brands.map((brand, index) => (
                    <ListItem key={index}>
                      <ListItemIcon>
                        <Avatar sx={{ bgcolor: 'primary.main' }}>
                          {brand.brand.charAt(0)}
                        </Avatar>
                      </ListItemIcon>
                      <ListItemText
                        primary={brand.brand}
                        secondary={`${brand.count} incidents`}
                      />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </StyledCard>
          </Grid>

          <Grid item xs={12} md={6}>
            <StyledCard>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  <Report sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Recommendations
                </Typography>
                <List>
                  {analyticsData.insights.improvement_recommendations.map((recommendation, index) => (
                    <ListItem key={index}>
                      <ListItemIcon>
                        <Shield color="primary" />
                      </ListItemIcon>
                      <ListItemText primary={recommendation} />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </StyledCard>
          </Grid>
        </Grid>
      )}

      {/* Risk Assessment */}
      {riskData && (
        <Box mt={4}>
          <Typography variant="h5" gutterBottom fontWeight="bold">
            Risk Assessment
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <MetricCard>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Overall Risk Score
                  </Typography>
                  <Typography variant="h3" fontWeight="bold">
                    {riskData.overall_risk_score.toFixed(1)}
                  </Typography>
                  {renderProgressBar(riskData.overall_risk_score, 10, 'secondary')}
                </CardContent>
              </MetricCard>
            </Grid>
            <Grid item xs={12} md={6}>
              <StyledCard>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Risk Factors
                  </Typography>
                  <List>
                    {riskData.risk_factors.map((factor, index) => (
                      <ListItem key={index}>
                        <ListItemText
                          primary={factor.factor}
                          secondary={factor.description}
                        />
                        <Chip
                          label={factor.score.toFixed(1)}
                          color={factor.score > 7 ? 'error' : factor.score > 4 ? 'warning' : 'success'}
                          size="small"
                        />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </StyledCard>
            </Grid>
          </Grid>
        </Box>
      )}
    </Container>
  );
};

export default AnalyticsPage; 